import { Box, ButtonMenu, ButtonMenuItem, Flex, Text } from '@pancakeswap/uikit';
import { useTranslation } from 'contexts/Localization';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CandleChart from 'views/Info/components/InfoCharts/CandleChart';
import NoChartAvailable from './NoChartAvailable';
import TokenDisplay from './TokenDisplay';

interface PricePoint {
  time: number;
  value: number;
}

const BasicChart = ({
  token0Address,
  token1Address,
  isChartExpanded,
  inputCurrency,
  outputCurrency,
  isMobile,
  currentSwapPrice,
}) => {
  const [timeWindow, setTimeWindow] = useState(0);
  const [mychart, setChart] = useState(<div />);
  const [isBadData, setBadData] = useState(false);
  const [hoverValue, setHoverValue] = useState<number | undefined>();
  const [hoverDate, setHoverDate] = useState<string | undefined>();

  const chartHeight = isChartExpanded ? 'calc(100% - 120px)' : '310px';
  const {
    t,
    currentLanguage: { locale },
  } = useTranslation();
  const currentDate = new Date().toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  async function setChartData(pairPrices: PricePoint[]) {
    const isbad =
      !Array.isArray(pairPrices) ||
      pairPrices.length <= 2 ||
      pairPrices.every((p) => !p?.value || p.value === Infinity || Number.isNaN(p.value));
    setBadData(isbad);

    if (isbad) return;

    const candles: any[] = [];
    const tstep = timeWindow === 2 ? 4799 : timeWindow === 1 ? 1199 : 299;

    candles.push({
      time: pairPrices[0].time,
      open: pairPrices[0].value,
      high: pairPrices[0].value,
      low: pairPrices[0].value,
      close: pairPrices[0].value,
    });

    let steps = [pairPrices[0]];
    for (let i = 1; i < pairPrices.length; i++) {
      steps.push(pairPrices[i]);
      if (pairPrices[i].time >= candles[candles.length - 1].time + tstep) {
        candles.push({
          time: steps[0].time,
          open: steps[0].value,
          high: Math.max(...steps.map((x) => x.value)),
          low: Math.min(...steps.map((x) => x.value)),
          close: steps[steps.length - 1].value,
        });
        steps = [pairPrices[i]];
      }
    }

    const valueToDisplay = hoverValue || candles[candles.length - 1].close;
    setChart(
      <>
        <Flex flexDirection="row" justifyContent="space-between" px="24px">
          <Flex flexDirection="column" pt="12px">
            <TokenDisplay
              value={candles.length > 0 && valueToDisplay}
              inputSymbol={inputCurrency?.symbol}
              outputSymbol={outputCurrency?.symbol}
            />
            <Text small color="secondary">{hoverDate || currentDate}</Text>
          </Flex>
          <Box>
            <ButtonMenu activeIndex={timeWindow} onItemClick={setTimeWindow} scale="sm">
              <ButtonMenuItem>{t('15 Min')}</ButtonMenuItem>
              <ButtonMenuItem>{t('1 Hr')}</ButtonMenuItem>
              <ButtonMenuItem>{t('4 Hr')}</ButtonMenuItem>
            </ButtonMenu>
          </Box>
        </Flex>
        <Box height={isMobile ? '100%' : chartHeight} p={isMobile ? '0px' : '16px'} width="100%">
          <CandleChart data={candles} />
        </Box>
      </>
    );
  }

  async function loadChartData(): Promise<PricePoint[]> {
    const chainId = 'vsc';
    const apiKey = '3w0C4VFsJO1ozm1W3ESkV5AYPeDPKZTMlTRMa9V1';
    const fallbackPoolAddress = '0xdbd23389df918176abf0e6f73cadefbbbac93d87'; // VSG/USDC

    try {
      const poolListRes = await axios.get(
        `https://public-api.dextools.io/trial/v2/token/${chainId}/${token0Address}/pools`,
        {
          headers: { 'X-API-KEY': apiKey },
        }
      );

      const pools = poolListRes.data.results || [];
      const pool = pools.find((p) => {
        const a = p.mainToken.address.toLowerCase();
        const b = p.sideToken.address.toLowerCase();
        const t0 = token0Address.toLowerCase();
        const t1 = token1Address.toLowerCase();
        return (a === t0 && b === t1) || (a === t1 && b === t0);
      });

      const poolAddress = pool?.address || fallbackPoolAddress;
      console.log('Using pool address:', poolAddress);

      const priceRes = await axios.get(
        `https://public-api.dextools.io/trial/v2/pool/${chainId}/${poolAddress}/price`,
        {
          headers: { 'X-API-KEY': apiKey },
        }
      );

      const prices = priceRes.data;
      const now = Date.now();
      const hour = 60 * 60 * 1000;

      const values: PricePoint[] = [];

      if (prices?.price24h) values.push({ time: now - 24 * hour, value: prices.price24h });
      if (prices?.price6h) values.push({ time: now - 6 * hour, value: prices.price6h });
      if (prices?.price1h) values.push({ time: now - 1 * hour, value: prices.price1h });
      if (prices?.price) values.push({ time: now, value: prices.price });

      if (values.length < 2) {
        console.warn('Insufficient price data from DEXTools:', prices);
        setBadData(true);
        return [];
      }

      return values;
    } catch (err) {
      console.error('DEXTools chart error:', err);
      setBadData(true);
      return [];
    }
  }

  useEffect(() => {
    loadChartData().then((data) => {
      setChartData(data);
    });
  }, [token0Address, token1Address, timeWindow]);

  if (isBadData) {
    return (
      <NoChartAvailable
        token0Address={token0Address}
        token1Address={token1Address}
        isMobile={isMobile}
      />
    );
  }

  return <>{mychart}</>;
};

export default BasicChart;
